/**
 * ParallelTimeline — horizontal timeline showing parallel execution lanes.
 *
 * Each task gets a horizontal bar on its own lane. Bars are colored by status
 * (green=completed, amber=running, red=failed, grey=pending/cancelled).
 * Clicking a bar opens the NodeDetailPanel.
 *
 * Used in the Intelligent Execution playground for visual timing breakdown.
 */

import { useMemo } from 'react'
import { clsx } from 'clsx'

export interface TimelineTask {
  id: string
  agentName: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  startMs: number   // relative to orchestration start
  durationMs: number
  group: number     // parallel group index
}

interface ParallelTimelineProps {
  tasks: TimelineTask[]
  totalDurationMs: number
  onTaskClick?: (taskId: string) => void
  className?: string
}

export default function ParallelTimeline({
  tasks,
  totalDurationMs,
  onTaskClick,
  className,
}: ParallelTimelineProps) {
  // Calculate time scale
  const maxTime = Math.max(totalDurationMs, 1000) // at least 1s
  const pctOf = (ms: number) => Math.min(100, (ms / maxTime) * 100)

  // Time markers (every 10s or so)
  const markers = useMemo(() => {
    const step = maxTime > 60000 ? 15000 : maxTime > 30000 ? 10000 : 5000
    const m: number[] = []
    for (let t = 0; t <= maxTime; t += step) m.push(t)
    return m
  }, [maxTime])

  const statusColor: Record<string, string> = {
    pending: 'bg-muted',
    running: 'bg-amber-500 animate-pulse',
    completed: 'bg-emerald-500',
    failed: 'bg-red-500',
    cancelled: 'bg-muted opacity-50',
  }

  if (tasks.length === 0) {
    return (
      <div className={clsx('bg-card rounded-lg border border-border p-4', className)}>
        <p className="text-xs text-muted-foreground text-center">No execution data</p>
      </div>
    )
  }

  return (
    <div className={clsx('bg-card rounded-lg border border-border p-4', className)}>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        Parallel Execution Timeline
      </p>

      {/* Time markers */}
      <div className="relative h-4 mb-1">
        {markers.map((t) => (
          <span
            key={t}
            className="absolute text-[10px] text-muted-foreground -translate-x-1/2"
            style={{ left: `${pctOf(t)}%` }}
          >
            {(t / 1000).toFixed(0)}s
          </span>
        ))}
      </div>

      {/* Lanes */}
      <div className="space-y-1.5 relative">
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none">
          {markers.map((t) => (
            <div
              key={t}
              className="absolute top-0 bottom-0 w-px bg-border/50"
              style={{ left: `${pctOf(t)}%` }}
            />
          ))}
        </div>

        {tasks.map((task) => {
          const left = pctOf(task.startMs)
          const width = Math.max(pctOf(task.durationMs), 1) // at least 1% visible
          return (
            <div
              key={task.id}
              className="flex items-center gap-2 h-7 relative"
            >
              {/* Label */}
              <span className="text-[10px] text-muted-foreground w-24 truncate text-right shrink-0">
                {task.agentName}
              </span>

              {/* Bar container */}
              <div className="flex-1 relative h-full bg-muted/30 rounded">
                <button
                  onClick={() => onTaskClick?.(task.id)}
                  className={clsx(
                    'absolute h-full rounded transition-all',
                    statusColor[task.status] || 'bg-muted',
                    onTaskClick && 'cursor-pointer hover:brightness-110',
                  )}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    minWidth: '4px',
                  }}
                  title={`${task.id}: ${task.status} (${(task.durationMs / 1000).toFixed(1)}s)`}
                />
              </div>

              {/* Duration label */}
              <span className="text-[10px] text-muted-foreground w-12 shrink-0">
                {task.durationMs > 0 ? `${(task.durationMs / 1000).toFixed(1)}s` : '-'}
              </span>
            </div>
          )
        })}
      </div>

      {/* Total */}
      <div className="mt-2 text-right text-xs text-muted-foreground">
        Total: {(totalDurationMs / 1000).toFixed(1)}s
      </div>
    </div>
  )
}
